import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFulfiller } from '../FulfillerContext';
import { getEntityType } from '../services/entity.management.service';
import { EntityType } from '../models/EntityType';
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  Copy,
  NavTab,
  NavTabItem,
  Spinner,
  Tag,
  Tooltip,
} from '@cimpress/react-components';
import { LandingPage } from './LandingPage';
import DetailView, { Detail, DetailViewProps } from '../components/DetailView';
import {
  formatRelativeTime,
  convertFromEntityTypeAttributes,
  toTitleCase,
} from '../utilities/common';
import '../css/common.css';
import '../css/detail.view.css';
import styles from '../css/pages.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import NotFoundState from '../components/common/NotFoundState';
import { sortByStatusOrder } from '@/utilities/sortStage';

enum EntityTypeInfoTab {
  GENERAL = 'General',
  ATTRIBUTES = 'Attributes',
  STAGES = 'Stages',
  RELATIONSHIPS = 'Relationships',
}

const EntityTypePage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const tenantParams = searchParams.get('tenantId');
  const navigate = useNavigate();
  const [entityType, setEntityType] = useState<EntityType | null>(null);
  const { fulfillerData } = useFulfiller();
  const [loading, setLoading] = useState(true);
  const [selectedEntityTypeTab, setSelectedEntityTypeTab] =
    useState<EntityTypeInfoTab>(EntityTypeInfoTab.GENERAL);
  const canEdit = useSelector((state: RootState) => state.permission.update);
  const tenantId = fulfillerData?.fulfillerLocation.value;
  const entityTypeId = params.entityTypeId;
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (
      !tenantId ||
      !entityTypeId ||
      !tenantParams ||
      tenantId !== tenantParams
    ) {
      setEntityType(null);
      setIsNotFound(true);
      setLoading(false);
      return;
    }

    let isActive = true;
    setLoading(true);
    setEntityType(null);
    setIsNotFound(false);

    getEntityType(tenantId, entityTypeId)
      .then((fetchedEntityType) => {
        if (!isActive) {
          return;
        }
        if (!fetchedEntityType) {
          setIsNotFound(true);
          return;
        }
        setEntityType(fetchedEntityType);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setIsNotFound(true);
      })
      .finally(() => {
        if (isActive) {
          setLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [tenantId, entityTypeId, tenantParams]);

  useEffect(() => {
    setSelectedEntityTypeTab(EntityTypeInfoTab.GENERAL);
  }, [params.entityTypeId]);

  if (!fulfillerData) {
    return <LandingPage></LandingPage>;
  }

  if (loading) {
    return <Spinner />;
  }
  if (isNotFound || !entityType) {
    const pageLink = tenantId ? `/entitytypes?tenantId=${tenantId}` : '/';
    return (
      <NotFoundState
        message={`Unable to find entity type with ID ${params.entityTypeId} at tenant ${fulfillerData.fulfillerLocation.value}.`}
        navigationLink={{ to: pageLink, text: 'Entity Types' }}
      />
    );
  }

  let entityTypeTabDetails = <div>None</div>;
  switch (selectedEntityTypeTab) {
    case EntityTypeInfoTab.ATTRIBUTES: {
      if (entityType.entityTypeAttributes?.properties) {
        const attributes = convertFromEntityTypeAttributes(
          entityType.entityTypeAttributes,
          true,
        );
        const mapAttributesToDetails = (attrs: typeof attributes): Detail[] => {
          return attrs.map((attr) => ({
            key: attr.name,
            value: attr.type,
            children: attr.children
              ? mapAttributesToDetails(attr.children)
              : undefined,
            style: styles.attributeTypeTag,
          }));
        };
        const props: DetailViewProps = {
          detailName: EntityTypeInfoTab.ATTRIBUTES,
          detailList: mapAttributesToDetails(attributes),
        };
        entityTypeTabDetails = <DetailView {...props} />;
      }
      break;
    }
    case EntityTypeInfoTab.STAGES:
      if (entityType.statusMapping) {
        const list: Detail[] = sortByStatusOrder(entityType.statusMapping).map(
          (sm) => {
            return {
              key: sm.stage,
              value: sm.status,
              style: styles[sm.status + 'Status'],
            };
          },
        );
        const props: DetailViewProps = {
          detailName: EntityTypeInfoTab.STAGES,
          detailList: list,
        };
        entityTypeTabDetails = <DetailView {...props} />;
      }
      break;
    case EntityTypeInfoTab.RELATIONSHIPS:
      if (entityType.entityTypeRelationships) {
        const list: Detail[] = entityType.entityTypeRelationships.map((etr) => {
          if (etr.relatedEntityTypeSourceSystem == 'external') {
            return {
              key: etr.relatedEntityTypeName ?? etr.relatedEntityTypeId,
              value: '',
            };
          }

          return {
            key: etr.relatedEntityTypeName ?? etr.relatedEntityTypeId,
            value: '',
            link: `/entitytypes/${etr.relatedEntityTypeId}?tenantId=${fulfillerData.fulfillerLocation.value}`,
          };
        });
        const props: DetailViewProps = {
          detailName: EntityTypeInfoTab.RELATIONSHIPS,
          detailList: list,
        };
        entityTypeTabDetails = <DetailView {...props} />;
      }
      break;
    default:
      entityTypeTabDetails = (
        <div>
          <h5>
            <b>NAME</b>
          </h5>
          {toTitleCase(entityType.entityTypeName)}
          <h5>
            <b>TAGS</b>
          </h5>
          {entityType.tags?.map((tag) => (
            <Tag key={tag} value={tag} className={styles.tag} />
          ))}
          <h5>
            <b>DESCRIPTION</b>
          </h5>
          {entityType.description}
        </div>
      );
  }

  return (
    <div className={styles.entityTypePage}>
      <Breadcrumbs className="breadcrumbs">
        <BreadcrumbItem className="breadcrumb-item">
          <Button
            variant="anchor"
            onClick={() =>
              navigate(
                `/entitytypes?tenantId=${fulfillerData.fulfillerLocation.value}`,
              )
            }
          >
            <div className="breadcrumb-item-text-link">Entity Types</div>
          </Button>
        </BreadcrumbItem>
        <BreadcrumbItem className="breadcrumb-item">
          <div className="breadcrumb-item-text">View Entity Type</div>
        </BreadcrumbItem>
      </Breadcrumbs>
      <div className={styles.entityTypeTopRow}>
        <div className={styles.entityTypeNameContainer}>
          <h1>{toTitleCase(entityType.entityTypeName)}</h1>
          <span className={styles.entityTypeNameDivider}></span>
          <span className="breadcrumb-item-text">
            <b>TYPE ID</b> &emsp;
            <Copy
              className={styles.copyButton}
              value={entityType.entityTypeId}
            />
          </span>
          <span
            className={styles.entityTypeNameDivider}
            style={{ marginLeft: '20px' }}
          ></span>
          <span className="breadcrumb-item-text">
            <b>VERSION ID</b> &emsp;
            <Copy className={styles.copyButton} value={entityType.versionId} />
          </span>
        </div>
        <div>
          <span>
            <b>Last Updated:</b>{' '}
            {formatRelativeTime(
              entityType.updatedTimestamp ?? entityType.createdTimestamp,
            )}
          </span>
          <Button
            className={styles.entityTypeDeleteButton}
            variant="secondary"
            disabled
          >
            Delete
          </Button>
          {canEdit ? (
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/entitytypes/${params.entityTypeId}/edit?tenantId=${fulfillerData.fulfillerLocation.value}`,
                )
              }
            >
              Edit
            </Button>
          ) : (
            <Tooltip
              contents="You do not have permission to edit entity type"
              direction="left"
            >
              <Button variant="primary" disabled={true}>
                Edit
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={styles.entityTypeNavTabContainer}>
        <NavTab>
          <NavTabItem
            active={selectedEntityTypeTab === EntityTypeInfoTab.GENERAL}
          >
            <button
              onClick={() =>
                setSelectedEntityTypeTab(EntityTypeInfoTab.GENERAL)
              }
            >
              General
            </button>
          </NavTabItem>
          <NavTabItem
            active={selectedEntityTypeTab === EntityTypeInfoTab.ATTRIBUTES}
          >
            <button
              onClick={() =>
                setSelectedEntityTypeTab(EntityTypeInfoTab.ATTRIBUTES)
              }
            >
              Attributes
            </button>
          </NavTabItem>
          <NavTabItem
            active={selectedEntityTypeTab === EntityTypeInfoTab.STAGES}
          >
            <button
              onClick={() => setSelectedEntityTypeTab(EntityTypeInfoTab.STAGES)}
            >
              Stages
            </button>
          </NavTabItem>
          <NavTabItem
            active={selectedEntityTypeTab === EntityTypeInfoTab.RELATIONSHIPS}
          >
            <button
              onClick={() =>
                setSelectedEntityTypeTab(EntityTypeInfoTab.RELATIONSHIPS)
              }
            >
              Relationships
            </button>
          </NavTabItem>
        </NavTab>
        {entityTypeTabDetails}
      </div>
    </div>
  );
};

export default EntityTypePage;
